import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { shotListAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Camera, CheckCircle2, Link2, Copy, X, Image as ImageIcon, User, Sparkles } from 'lucide-react';

const CATEGORIES = [
  { key: 'must_have', label: 'Must Have' },
  { key: 'getting_ready', label: 'Getting Ready' },
  { key: 'ceremony', label: 'Ceremony' },
  { key: 'family', label: 'Family' },
  { key: 'couple', label: 'Couple' },
  { key: 'details', label: 'Details' },
  { key: 'reception', label: 'Reception' },
  { key: 'candid', label: 'Candid' },
  { key: 'other', label: 'Other' },
];

const CAT_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.key, c.label]));

const ShotListAdmin = ({ eventId, eventName }) => {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState(null);
  const [addingCategory, setAddingCategory] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [uploadingFor, setUploadingFor] = useState(null); // item_id or 'new'
  const [pendingImagePath, setPendingImagePath] = useState(null);
  const [savingPublic, setSavingPublic] = useState(false);

  const refetch = useCallback(async () => {
    try {
      const r = await shotListAPI.get(eventId);
      setList(r.data);
    } catch (e) {
      console.error('Failed to load shot list', e);
      toast.error('Failed to load shot list');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { refetch(); }, [refetch]);

  const handleAdd = async (category) => {
    if (!newTitle.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await shotListAPI.addItem(eventId, {
        title: newTitle.trim(),
        description: newDesc.trim(),
        category,
        reference_image_path: pendingImagePath,
      });
      setNewTitle('');
      setNewDesc('');
      setPendingImagePath(null);
      setAddingCategory(null);
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add shot');
    }
  };

  const handleToggleCaptured = async (item) => {
    try {
      await shotListAPI.updateItem(item.item_id, { captured: !item.captured });
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete shot "${item.title}"?`)) return;
    try {
      await shotListAPI.deleteItem(item.item_id);
      refetch();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleUploadRef = async (file, targetItem) => {
    if (!file) return;
    setUploadingFor(targetItem ? targetItem.item_id : 'new');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await shotListAPI.uploadReference(eventId, formData);
      if (targetItem) {
        await shotListAPI.updateItem(targetItem.item_id, { reference_image_path: r.data.path });
        refetch();
      } else {
        setPendingImagePath(r.data.path);
      }
      toast.success('Reference image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleTogglePublicLink = async () => {
    setSavingPublic(true);
    try {
      if (list?.public_enabled) {
        await shotListAPI.disablePublicLink(eventId);
        toast.success('Public link disabled');
      } else {
        await shotListAPI.enablePublicLink(eventId);
        toast.success('Public link enabled — share with the couple');
      }
      refetch();
    } catch (e) {
      toast.error('Failed to update public link');
    } finally {
      setSavingPublic(false);
    }
  };

  const publicUrl = useMemo(() => {
    if (!list?.public_enabled || !list?.public_token) return '';
    return `${window.location.origin}/shotlist/${list.public_token}`;
  }, [list]);

  if (loading) {
    return <div className="text-sm text-muted-foreground p-4" data-testid="shotlist-loading">Loading shot list…</div>;
  }

  const stats = list?.stats || { total: 0, captured: 0, remaining: 0, percent: 0 };

  return (
    <div className="space-y-5" data-testid="shotlist-admin">
      {/* Header + Stats + Public Link */}
      <div className="card p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-medium flex items-center gap-2">
              <Camera size={18} className="text-gold" />
              Shot List
              {eventName && <span className="text-sm text-muted-foreground font-normal">— {eventName}</span>}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {stats.total} shot{stats.total !== 1 ? 's' : ''} planned · {stats.captured} captured · {stats.remaining} remaining
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-32 h-2 bg-muted overflow-hidden">
              <div className="h-full bg-gold transition-all" style={{ width: `${stats.percent}%` }} />
            </div>
            <span className="text-sm font-mono w-12 text-right">{stats.percent}%</span>
          </div>
        </div>

        {/* Public link toggle */}
        <div className="border-t border-border pt-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-medium flex items-center gap-1.5">
                <Link2 size={14} className="text-gold" /> Couple submission link
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Share with the bride & groom so they can add their own must-have shots.
              </p>
              {list?.public_enabled && publicUrl && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    readOnly
                    value={publicUrl}
                    onClick={(e) => e.target.select()}
                    className="flex-1 min-w-0 px-2 py-1.5 text-xs bg-muted border border-input bg-transparent text-foreground placeholder:text-muted-foreground font-mono"
                    data-testid="shotlist-public-url"
                  />
                  <button
                    onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border border-border hover:bg-accent"
                    data-testid="shotlist-copy-link"
                  >
                    <Copy size={12} /> Copy
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handleTogglePublicLink}
              disabled={savingPublic}
              className={`px-3 py-2 text-xs uppercase tracking-wide whitespace-nowrap transition-colors ${
                list?.public_enabled
                  ? 'border border-red-300 text-red-600 dark:text-red-400 hover:bg-red-50 dark:bg-red-500/10'
                  : 'bg-primary text-primary-foreground hover:bg-black/90'
              } disabled:opacity-50`}
              data-testid="shotlist-toggle-public"
            >
              {list?.public_enabled ? 'Disable Link' : 'Enable Public Link'}
            </button>
          </div>
        </div>
      </div>

      {/* Categories */}
      {CATEGORIES.map(({ key, label }) => {
        const items = (list?.by_category?.[key] || []);
        if (items.length === 0 && addingCategory !== key) {
          return (
            <div key={key} className="flex items-center justify-between border-b border-dashed border-border pb-1" data-testid={`shotlist-cat-${key}-empty`}>
              <p className="text-sm text-muted-foreground uppercase tracking-wide">{label}</p>
              <button
                onClick={() => { setAddingCategory(key); setNewTitle(''); setNewDesc(''); setPendingImagePath(null); }}
                className="text-xs text-gold hover:text-gold/80 inline-flex items-center gap-1"
                data-testid={`shotlist-add-${key}`}
              >
                <Plus size={12} /> Add shot
              </button>
            </div>
          );
        }
        return (
          <div key={key} className="card p-4" data-testid={`shotlist-cat-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium uppercase tracking-wide flex items-center gap-2">
                {label}
                <span className="text-xs text-muted-foreground normal-case font-normal">({items.length})</span>
              </h4>
              <button
                onClick={() => { setAddingCategory(key); setNewTitle(''); setNewDesc(''); setPendingImagePath(null); }}
                className="text-xs text-gold hover:text-gold/80 inline-flex items-center gap-1"
                data-testid={`shotlist-add-${key}`}
              >
                <Plus size={12} /> Add
              </button>
            </div>

            {/* Add form */}
            {addingCategory === key && (
              <div className="border border-gold/50 bg-gold/5 p-3 mb-3 space-y-2" data-testid={`shotlist-add-form-${key}`}>
                <input
                  type="text"
                  autoFocus
                  placeholder="Shot title (e.g., First look reveal)"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-input bg-background"
                  data-testid="shotlist-new-title"
                />
                <textarea
                  placeholder="Notes / direction (optional)"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1.5 text-sm border border-input bg-background"
                  data-testid="shotlist-new-desc"
                />
                {pendingImagePath && (
                  <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-400">
                    <CheckCircle2 size={14} /> Reference image ready
                    <button onClick={() => setPendingImagePath(null)} className="text-muted-foreground hover:text-foreground"><X size={12} /></button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1.5 text-xs border border-border hover:bg-accent">
                    <ImageIcon size={12} />
                    {uploadingFor === 'new' ? 'Uploading…' : 'Add reference photo'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingFor === 'new'}
                      onChange={(e) => handleUploadRef(e.target.files?.[0], null)}
                      data-testid="shotlist-upload-new"
                    />
                  </label>
                  <div className="flex-1" />
                  <button
                    onClick={() => { setAddingCategory(null); setNewTitle(''); setNewDesc(''); setPendingImagePath(null); }}
                    className="px-3 py-1.5 text-xs border border-border hover:bg-accent"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAdd(key)}
                    className="px-3 py-1.5 text-xs bg-primary text-primary-foreground hover:bg-black/90"
                    data-testid={`shotlist-save-${key}`}
                  >
                    Save Shot
                  </button>
                </div>
              </div>
            )}

            {/* Items */}
            <div className="space-y-2">
              {items.map((item) => (
                <ShotItemRow key={item.item_id} item={item} onToggle={handleToggleCaptured} onDelete={handleDelete} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ShotItemRow = ({ item, onToggle, onDelete }) => (
  <div
    className={`flex items-start gap-3 p-2.5 border ${item.captured ? 'border-green-200 dark:border-green-500/30 bg-green-50/40' : 'border-border'}`}
    data-testid={`shotlist-item-${item.item_id}`}
  >
    <button
      onClick={() => onToggle(item)}
      className={`flex-shrink-0 w-5 h-5 mt-0.5 border-2 flex items-center justify-center transition-colors ${
        item.captured ? 'bg-green-600 border-green-600' : 'border-border hover:border-gold'
      }`}
      data-testid={`shotlist-toggle-${item.item_id}`}
      aria-label={item.captured ? 'Mark as not captured' : 'Mark as captured'}
    >
      {item.captured && <CheckCircle2 size={14} className="text-white" />}
    </button>
    {item.reference_image_path && (
      <ShotThumb path={item.reference_image_path} />
    )}
    <div className="flex-1 min-w-0">
      <p className={`text-sm ${item.captured ? 'line-through text-muted-foreground' : 'font-medium'}`}>{item.title}</p>
      {item.description && <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>}
      <div className="flex items-center gap-2 mt-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {item.source === 'client' && (
          <span className="inline-flex items-center gap-0.5 text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-500/10 px-1.5 py-0.5">
            <Sparkles size={10} /> From couple
          </span>
        )}
        <span className="inline-flex items-center gap-0.5">
          <User size={10} /> {item.submitted_by_name || '—'}
        </span>
      </div>
    </div>
    <button
      onClick={() => onDelete(item)}
      className="text-muted-foreground hover:text-red-600 dark:text-red-400 p-1"
      data-testid={`shotlist-delete-${item.item_id}`}
      aria-label="Delete shot"
    >
      <Trash2 size={14} />
    </button>
  </div>
);

const ShotThumb = ({ path }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let active = true;
    const token = localStorage.getItem('token');
    const filename = path.replace(/^.*\/shots\//, '');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/shots/${filename}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.blob() : null)
      .then(b => { if (active && b) setBlobUrl(URL.createObjectURL(b)); })
      .catch(() => {});
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  return (
    <div className="flex-shrink-0 w-14 h-14 bg-muted border border-border overflow-hidden">
      {blobUrl ? (
        <img src={blobUrl} alt="reference" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          <ImageIcon size={16} />
        </div>
      )}
    </div>
  );
};

export { CAT_LABEL };
export default ShotListAdmin;
